import { Snackbar } from '@material-ui/core';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSearchParam } from 'react-use';

import { useAppDispatch } from '../../hooks';
import { loadUserProfile, UserSelectors } from '../../reducers/user';
import {
    clearAvailableWorkError,
    loadAvailableWork,
    WorkSelectors
} from '../../reducers/work';
import { Wrapper } from '../../shared';
import { isUserOffline } from '../../utils/userStatus';
import { EmptyView, OfflineView, WorkView } from './components';

type Props = Record<string, never>;

const Work: FunctionComponent<Props> = ({ }: Props) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const location = useLocation();

    const workIdSearchParam = useSearchParam('workId');
    const specificWorkId = workIdSearchParam ? parseInt(workIdSearchParam) : undefined;
    const openConversationParam = useSearchParam('openConversation');
    const openConversation = openConversationParam === 'true';

    const { profile } = UserSelectors();
    const { work, workLoading, workError, workFetched } = WorkSelectors();

    const [ specificWorkError, setSpecificWorkError ] = useState<string | null>(null);
    const [ profileChecked, setProfileChecked ] = useState(false);

    const isOffline = isUserOffline(profile?.status) || workError === 'user_offline';
    const isInitialLoading = !profileChecked || (workLoading && !isOffline);

    useEffect(() => {
        dispatch(loadUserProfile({})).finally(() => setProfileChecked(true));
    }, [ dispatch ]);

    useEffect(() => {
        if (!profileChecked || isUserOffline(profile?.status)) {
            return;
        }

        dispatch(
            loadAvailableWork({ specificWorkId })
        ).unwrap().then((payload: any) => {
            if (payload.userOffline) {
                return;
            }

            if (specificWorkId &&
                (payload.work === null || payload.work.id !== specificWorkId)) {
                if (!!payload.workRecord) {
                    setSpecificWorkError(
                        'You have in-process work. Finish or cancel it to access other work'
                    );
                } else {
                    setSpecificWorkError('The requested work item is currently unavailable');
                }
            }
        });
    }, [ dispatch, profileChecked, profile?.status, specificWorkId ]);

    useEffect(() => {
        if (specificWorkId && work && work.id !== specificWorkId) {
            history.push(location.pathname);
        }
    }, [ specificWorkId, work, history, location.pathname ]);

    const renderContent = () => {
        if (!profileChecked) {
            return null;
        }

        if (isOffline) {
            return <OfflineView />;
        }

        if (work !== null) {
            return <WorkView work={work} openConversation={openConversation} />;
        }

        if (workFetched) {
            return (
                <>
                    <EmptyView />
                    <Snackbar
                        open={!!workError}
                        autoHideDuration={6000}
                        onClose={() => dispatch(clearAvailableWorkError())}
                        message="Woops! Not sure what happened there... Please try again"
                    />
                </>
            );
        }

        return null;
    };

    return (
        <Wrapper loading={isInitialLoading}>
            {renderContent()}
            <Snackbar
                open={!!specificWorkError}
                autoHideDuration={6000}
                onClose={() => setSpecificWorkError(null)}
                message={specificWorkError}
            />
        </Wrapper>
    );
};

export default Work;
